import React from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import type { BundleWithStatus } from '@features/subscriptions/types';
import { Box, Typography } from '@mui/material';

interface DataSecurityPurchaseDialogProps {
  open: boolean;
  bundle: BundleWithStatus;
  billingInterval: 'month' | 'year';
  organizationSize?: number;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export const DataSecurityPurchaseDialog: React.FC<DataSecurityPurchaseDialogProps> = ({
  open,
  bundle,
  billingInterval,
  organizationSize,
  loading = false,
  onClose,
  onConfirm,
}) => {
  const price = billingInterval === 'month' ? bundle.monthly_unit_amount : bundle.yearly_unit_amount;
  const formattedPrice = (price / 100).toFixed(0);
  const isMultiUser = organizationSize && organizationSize > 1;
  const totalCost = price * (organizationSize || 1);
  const formattedTotalCost = (totalCost / 100).toFixed(0);
  const intervalLabel = billingInterval === 'month' ? 'month' : 'year';

  const baseMessage =
    'This bundle provides enhanced security and compliance features including a Business Associate Agreement (BAA), custom data retention controls, and Certificate of Insurance.';

  return (
    <ConfirmDialog
      open={open}
      variant="info"
      title="Add Data & Security Bundle"
      message={baseMessage}
      onClose={onClose}
      onConfirm={onConfirm}
      confirmText="Add to Plan"
      loading={loading}
    >
      <Box sx={{ mt: 2 }}>
        {/* Pricing Information */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Pricing:
          </Typography>
          {isMultiUser ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                ${formattedPrice}/{intervalLabel} per user
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Your organization: {organizationSize} users
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Estimated {billingInterval === 'month' ? 'monthly' : 'annual'} cost: ${formattedTotalCost}/{intervalLabel}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ${formattedPrice}/{intervalLabel}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This amount will be added to your next billing cycle.
          </Typography>
        </Box>

        {/* Features */}
        {bundle.marketing_features && bundle.marketing_features.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              What&apos;s included:
            </Typography>
            {bundle.marketing_features.slice(0, 3).map((feature, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5, pl: 2 }}>
                • {feature.name}
              </Typography>
            ))}
            {bundle.marketing_features.length > 3 && (
              <Typography variant="body2" sx={{ pl: 2, color: 'text.secondary' }}>
                • +{bundle.marketing_features.length - 3} more features
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </ConfirmDialog>
  );
};
